import {
    CREATE,
    DELETE,
    DELETE_MANY,
    fetchUtils,
    GET_LIST,
    GET_MANY,
    GET_MANY_REFERENCE,
    GET_ONE,
    UPDATE,
    UPDATE_MANY
} from "react-admin";
import moment from "moment";

/**
 * Maps react-admin queries to a REST API implemented using Java Spring Boot and Swagger
 *
 * @example
 * GET_LIST     => GET http://my.api.url/posts?page=0&size=10&sort=id,desc
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?id=1234&id=5678
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts/123
 */

// Module-scope (outside the factory closure below) so screens can import these
// directly — Task 3's duplicate-email attach recipe needs getGuardianV2JsonData
// outside this factory, and CharityEditStatamic needs isUuid.
export const isUuid = (id) => String(id).includes('-');

const GUARDIAN_MANAGED_KEYS = [
    'enabled', 'email', 'given_name', 'family_name', 'salutation', 'phone', 'street',
    'house_number', 'postal_code', 'city', 'country', 'donation_method', 'iban', 'note',
    'direct_debit_reference', 'direct_debit_start_date', 'sponsor_as_company', 'company',
    'stripe_subscription_id', 'stripe_customer_id', 'metadata',
];

/**
 * v2 guardian write JSON. Allow-list only — this is what keeps read-only response
 * keys (_links, children, charity_model, timestamps, scope_charity_name, warning,
 * relation_number, firebase_status, sponsor_children, sponsored_child_count,
 * avatar*, initials) off the wire even though RA's edit form re-submits the whole
 * record (getFormInitialValues merges the record into form values, so every
 * key arrives in `data` on update).
 */
export const getGuardianV2JsonData = (data, {isCreate, previousData} = {}) => {
    const json = {};
    GUARDIAN_MANAGED_KEYS.forEach((key) => {
        if (data[key] !== undefined) json[key] = data[key];
    });

    // §8a auto payment-status transition: the server sets payment_status (and a
    // reason/date) itself when donation_method changes AND no payment_status key
    // is sent in the request. On update we therefore omit the pair when the form
    // left it untouched (both still equal previousData's stored values) so that
    // transition can fire; a changed value, or an explicit '' clear (D21), is sent
    // as-is. Create has no previousData to diff against — always send what's present.
    // The pair is sent together or not at all: a bare payment_status_reason with no
    // payment_status would still leave the payment_status key absent from the wire,
    // which races the §8a auto-transition (it fires on ANY request with no
    // payment_status key) against a reason the caller didn't intend to pin down.
    const paymentStatusTouched = isCreate
        || data.payment_status !== previousData?.payment_status
        || data.payment_status_reason !== previousData?.payment_status_reason;
    if (paymentStatusTouched && data.payment_status !== undefined) {
        json.payment_status = data.payment_status;
        if (data.payment_status_reason !== undefined) json.payment_status_reason = data.payment_status_reason;
    }

    if (isCreate) {
        if (data.password !== undefined) json.password = data.password;
        if (data.send_notification_email !== undefined) json.send_notification_email = data.send_notification_email;
        if (data.language !== undefined) json.language = data.language;
        // The form's hidden child_id input carries the v2 child's UUID external_id.
        // A guardian create without a child is a hard 400 server-side (§8a) — throw
        // client-side instead of firing a request we know will fail, and never fall
        // back to emitting a numeric child_id (no such param exists in v2).
        if (!data.child_id || !isUuid(data.child_id)) {
            throw new Error('Guardian create requires a child (child_id must be a UUID)');
        }
        json.child_external_id = String(data.child_id);
    } else if (data.scope_charity_external_id !== undefined) {
        // Selects which sponsored organisation's tier-3 row to write when the
        // guardian sponsors children in more than one organisation (§4);
        // prefilled from getOne or injected by the duplicate-email attach recipe.
        json.scope_charity_external_id = data.scope_charity_external_id;
    }

    return JSON.stringify(json);
};

/**
 * v2 volunteer write JSON — replaces the raw JSON.stringify(params.data), which
 * used to PUT back charity_model/_links/timestamps untouched.
 */
export const getVolunteerV2JsonData = (data, {isCreate} = {}) => {
    const json = {};
    ['email', 'given_name', 'family_name', 'enabled', 'realm_role'].forEach((key) => {
        if (data[key] !== undefined) json[key] = data[key];
    });
    if (isCreate) {
        if (data.password !== undefined) json.password = data.password;
        if (data.charity_external_id !== undefined) json.charity_external_id = data.charity_external_id;
    }
    // Optional on both verbs; when sent it REPLACES the coordinated set wholesale
    // (§8b). There is no primary-charity picker on edit, so update never sends
    // charity_external_id (matches the form) — only charity_external_ids.
    if (data.charity_external_ids !== undefined) json.charity_external_ids = data.charity_external_ids;
    return JSON.stringify(json);
};

/** v2 umbrella admin write JSON. */
export const getUmbrellaAdminV2JsonData = (data, {isCreate} = {}) => {
    const json = {};
    ['email', 'given_name', 'family_name', 'enabled', 'umbrella_external_id'].forEach((key) => {
        if (data[key] !== undefined) json[key] = data[key];
    });
    if (isCreate && data.password !== undefined) json.password = data.password;
    return JSON.stringify(json);
};

/**
 * v2 umbrella notification switches JSON (OUTCOME item 10). Only booleans are sent; an absent key
 * leaves the switch as it is server side.
 */
// Contract: nofam-api .ai/docs/integration/nofam-api-v2.md (/api/v2 admin routes, umbrella notification switches)
export const getUmbrellaNotificationsV2JsonData = (data) => {
    const json = {};
    ['send_new_post_mail', 'require_post_approval', 'send_registration_copy_to_projects'].forEach((key) => {
        if (typeof data[key] === 'boolean') json[key] = data[key];
    });
    return JSON.stringify(json);
};

/**
 * JSON body for the pending post text edit (#52): only `description` and, when given, `language_code`.
 * Leave `language_code` out to keep the language of the post's original.
 */
// Contract: nofam-api .ai/docs/integration/nofam-api-v2.md (/api/v2 admin routes, pending post text)
export const getPostDescriptionV2JsonData = (data) => {
    const json = {description: data.description};
    if (typeof data.language_code === 'string' && data.language_code.trim()) {
        json.language_code = data.language_code.trim();
    }
    return JSON.stringify(json);
};

const springDataRest = (apiUrl, httpClient = fetchUtils.fetchJson) => {

    // Mapped resources (with other names in the backend
    // NOTE: v2 kept the `umbrellas` key exactly, so this mapping also drives the v2 HAL parse
    // (both charities and umbrellas embed under `_embedded.charities`). `locations` needs no
    // entry here — its v2 response embeds under `_embedded.locations`, matching the resource
    // name as-is, so the default (unmapped) passthrough in mapResource() already resolves it.
    const resourceMapper = {
        umbrellas: "charities",
        "metadata-definitions": "metadata_definitions",
        "posts-pending": "posts", // Maps to posts but uses /posts/pending endpoint
        // Add more mappings as needed
    };

    function mapResource(resource, resourceMapper) {
        return resourceMapper[resource] || resource;
    }

    /**
     * Get the URL resource name for building API endpoints
     * Some resources need special handling (e.g., umbrellas should use /umbrellas not /charities)
     */
    function getUrlResource(resource) {
        // Resources that should NOT be mapped in URLs
        if (resource === 'umbrellas' || resource === 'metadata-definitions') {
            return resource;
        }
        // All other resources use the mapped name
        return mapResource(resource, resourceMapper);
    }

    // v2 API base (same host, /api/v2). Children single-record verbs (GET_LIST/GET_ONE(UUID)/
    // GET_MANY/CREATE/UPDATE/DELETE) live fully on v2 admin endpoints — UUID ids. Bulk verbs
    // (UPDATE_MANY/DELETE_MANY) are rejected outright — the v2 children endpoints are
    // single-record only and bulk children ops are unreachable from the UI today.
    // GET_MANY_REFERENCE remains v1 pending a future slice.
    const apiV2Url = apiUrl.replace(/\/api\/v1$/, '/api/v2');

    // The v2 children filters are UUID-keyed (umbrella_id holds a UUID, charity_external_id
    // replaces the numeric charity_id). A stale numeric charity_id / non-UUID umbrella_id can
    // still be present in a list's persisted filterValues (RaStore) — e.g. left over from
    // before this list adopted v2, or briefly on first mount before ScopeContext's self-heal
    // effect backfills the external id. Sending either as-is 400s the v2 endpoint, so strip
    // them defensively here rather than relying solely on the seeding effect upstream.
    const sanitizeChildrenFilter = (filter) => {
        const next = {...filter};
        delete next.charity_id;
        if (next.umbrella_id != null && !isUuid(next.umbrella_id)) {
            delete next.umbrella_id;
        }
        return next;
    };

    // Same defensive strip for the v2 charities list: useCharityChoices (and other stale
    // filterValues sources) can still pass a numeric umbrella scope id, which 400s the v2
    // charities endpoint. UUID values are kept as-is; only non-UUID values are dropped.
    const sanitizeCharitiesFilter = (filter) => {
        const next = {...filter};
        if (next.umbrella_id != null && !isUuid(next.umbrella_id)) {
            delete next.umbrella_id;
        }
        return next;
    };

    // Same defensive strip for the v2 guardians/volunteers/umbrellaAdmins lists.
    // Both umbrella_id and charity_id are UUID-valued for people (unlike children,
    // whose charity-level param is charity_external_id — people genuinely use the
    // name charity_id for their scope filter, per the contract's param-naming map).
    // A stale numeric value (leftover RaStore state from the old `true`/'umbrellaOnly'
    // scope modes) 400s the v2 endpoint and is stripped. charity_external_id is never
    // a valid people filter key and is always dropped. A role that maps to the
    // charitad rolePath (charity, volunteer, or anything unrecognized) gets BOTH scope
    // keys dropped outright — the charitad people endpoints 400 on any scope filter
    // at all (§5a). An umbrellad caller never sends umbrella_id at all — the anchor is
    // implicit server-side and an explicit one is 403/400 (§5a/§5c) — so it's dropped
    // outright for that role too, even a UUID one; only charity_id may survive for it.
    const sanitizePeopleFilter = (filter, role) => {
        const next = {...filter};
        delete next.charity_external_id;
        if (role !== 'admin' && role !== 'umbrella') {
            delete next.umbrella_id;
            delete next.charity_id;
            // The charity tier may not filter on payment method (400); a value can still
            // come from the list filter the browser saved before the filter was hidden.
            delete next.donation_method;
            return next;
        }
        if (role === 'umbrella') {
            delete next.umbrella_id;
        } else if (next.umbrella_id != null && !isUuid(next.umbrella_id)) {
            delete next.umbrella_id;
        }
        if (next.charity_id != null && !isUuid(next.charity_id)) {
            delete next.charity_id;
        }
        return next;
    };

    const isVerifiedManager = () => {
        const role = localStorage.getItem('role');
        return role === 'admin' || role === 'umbrella';
    };

    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        const role = localStorage.getItem('role');
        const rolePath = role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';

        // Map the resource name to the actual backend endpoint
        const mappedResource = mapResource(resource, resourceMapper);

        let url = "";
        const options = {};
        switch (type) {
            case GET_LIST: {
                const {page, perPage} = params.pagination;
                const pageMinOne = page - 1;
                const {field, order} = params.sort;
                // The charity tier may not sort sponsors on surname or email (400). A sort the
                // browser saved before the Sponsor column moved to given_name falls back to it.
                const camelField = resource === 'guardians' && role !== 'admin' && role !== 'umbrella'
                    && (toCamel(field) === 'familyName' || field === 'email')
                    ? 'givenName'
                    : toCamel(field);
                let filter = '';
                if (params && params.hasOwnProperty('filter')) {
                    const effectiveFilter = resource === 'children'
                        ? sanitizeChildrenFilter(params.filter)
                        : resource === 'charities'
                            ? sanitizeCharitiesFilter(params.filter)
                            : (resource === 'guardians' || resource === 'volunteers' || resource === 'umbrellaAdmins')
                                ? sanitizePeopleFilter(params.filter, role)
                                : params.filter;
                    for (let key in effectiveFilter) {
                        filter += '&' + encodeURIComponent(key) + '=' + encodeURIComponent(effectiveFilter[key]);
                    }
                }
                if (resource === 'children') {
                    url = `${apiV2Url}/${rolePath}/children?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                } else if (resource === 'posts-pending') {
                    url = `${apiUrl}/${rolePath}/posts/pending?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                } else if (resource === 'metadata-definitions') {
                    // Use hyphens in URL (NOT mappedResource which has underscores)
                    url = `${apiUrl}/${rolePath}/metadata-definitions?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                } else if (resource === 'charities') {
                    // Charities list lives fully on v2 for every role (UUID ids).
                    url = `${apiV2Url}/${rolePath}/charities?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                } else if (resource === 'umbrellas') {
                    // Umbrellas list is v2 for admin/umbrella roles only — there is no charitad
                    // umbrellas v2 path (and no charitad consumer in practice).
                    if (role !== 'charity') {
                        url = `${apiV2Url}/${rolePath}/umbrellas?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                    } else {
                        url = `${apiUrl}/${rolePath}/umbrellas?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                    }
                } else if (resource === 'guardians' || resource === 'volunteers' || resource === 'umbrellaAdmins') {
                    // People listings live fully on v2 for every role; the path segment
                    // mirrors v1 verbatim, including camelCase `umbrellaAdmins`.
                    url = `${apiV2Url}/${rolePath}/${resource}?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                } else if (resource === 'locations') {
                    // Locations v2 GET_LIST is admin/umbrellad only — no charitad v2 path, no
                    // charitad consumer. Sort is ignored server-side, so it's omitted here.
                    if (role !== 'charity') {
                        url = `${apiV2Url}/${rolePath}/locations?page=${pageMinOne}&size=${perPage}${filter}`;
                    } else {
                        url = `${apiUrl}/${rolePath}/${mappedResource}?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                    }
                } else {
                    url = `${apiUrl}/${rolePath}/${mappedResource}?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}${filter}`;
                }
                break;
            }
            case GET_ONE:
                if (resource === 'children' && isUuid(params.id)) {
                    url = `${apiV2Url}/${rolePath}/children/${params.id}`;
                    break;
                }
                // Numeric ids fall back to v1 (inbound legacy links); UUID ids route to v2.
                if (resource === 'charities' && isUuid(params.id)) {
                    url = `${apiV2Url}/${rolePath}/charities/${params.id}`;
                    break;
                }
                if (resource === 'umbrellas' && isUuid(params.id) && role !== 'charity') {
                    url = `${apiV2Url}/${rolePath}/umbrellas/${params.id}`;
                    break;
                }
                if (resource === 'guardians' || resource === 'volunteers' || resource === 'umbrellaAdmins') {
                    url = `${apiV2Url}/${rolePath}/${resource}/${params.id}`;
                    break;
                }
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}/${params.id}`;
                break;
            case GET_MANY: {
                const queryString = params.ids.map(id => `id=${encodeURIComponent(id)}`).join('&');
                if (resource === 'children') {
                    url = `${apiV2Url}/${rolePath}/children?${queryString}`;
                    break;
                }
                // Charities GET_MANY = bare array on v2 (silent omission for unknown ids).
                // Umbrellas has NO v2 GET_MANY (deliberate) — stays on v1.
                if (resource === 'charities') {
                    url = `${apiV2Url}/${rolePath}/charities?${queryString}`;
                    break;
                }
                // volunteers GET_MANY = bare array on v2 (consumer: charity
                // default_project_coordinator_id reference). guardians/umbrellaAdmins
                // have no v2 GET_MANY at all — rejected earlier, before this is reached.
                if (resource === 'volunteers') {
                    url = `${apiV2Url}/${rolePath}/volunteers?${queryString}`;
                    break;
                }
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}?${queryString}`;
                break;
            }
            case GET_MANY_REFERENCE: {
                const {page, perPage} = params.pagination;
                const pageMinOne = page - 1;
                const {field, order} = params.sort;
                const camelField = toCamel(field);
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}/many/${params.id}?page=${pageMinOne}&size=${perPage}&sort=${camelField},${order}`;
                break;
            }
            case UPDATE:
                // charities/umbrellas UPDATE is handled earlier (v2, multipart) before this
                // function is ever reached — see the dispatcher's early-return branches.
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}/${params.id}`;
                options.method = "PUT";
                if (resource === 'posts') {
                    options.body = getPostFormData(params.data);
                } else {
                    options.body = JSON.stringify(params.data);
                }
                break;
            case CREATE:
                // charities/umbrellas CREATE is handled earlier (v2, multipart) before this
                // function is ever reached — see the dispatcher's early-return branches.
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}`;
                if (resource === 'posts') {
                    options.body = getPostFormData(params.data);
                } else {
                    options.body = JSON.stringify(params.data);
                }
                options.method = "POST";

                break;
            case DELETE:
                if (resource === 'children') {
                    url = `${apiV2Url}/${rolePath}/children/${params.id}`;
                    options.method = 'DELETE';
                    break;
                }
                url = `${apiUrl}/${rolePath}/${getUrlResource(resource)}/${params.id}`;
                options.method = "DELETE";
                break;
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return {url, options};
    };

    /**
     * Multipart body for the v2 admin children endpoints.
     * verified is admin/umbrella-only (backend 400s if a charity role sends it).
     * metadata omitted entirely = server no-op; JSON string when present.
     * charity_external_id on update = re-parenting (same value is a no-op).
     */
    const getChildV2FormData = (childData) => {
        const formData = new FormData();
        if (childData.profile_image && childData.profile_image.rawFile) {
            formData.append('image', childData.profile_image.rawFile);
        }
        if (childData.profile_video && childData.profile_video.rawFile) {
            formData.append('video', childData.profile_video.rawFile);
        }
        const appendIfPresent = (key, value) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        };
        appendIfPresent('first_name', childData.first_name);
        appendIfPresent('last_name', childData.last_name);
        appendIfPresent('gender', childData.gender);
        appendIfPresent('intro', childData.intro);
        appendIfPresent('family', childData.family);
        appendIfPresent('help', childData.help);
        if (childData.birth_date !== undefined && childData.birth_date !== null) {
            formData.append('birth_date', moment(childData.birth_date).format('YYYY-MM-DD'));
        }
        formData.append('contact_frequency', childData.contact_frequency || 'MONTHLY');
        if (childData.project_level !== undefined && childData.project_level !== null) {
            formData.append('project_level', childData.project_level);
        }
        if (childData.charity_external_id) {
            formData.append('charity_external_id', childData.charity_external_id);
        }
        if (isVerifiedManager() && typeof childData.verified === 'boolean') {
            formData.append('verified', childData.verified);
        }
        if (childData.metadata && typeof childData.metadata === 'object') {
            formData.append('metadata', JSON.stringify(childData.metadata));
        }
        return formData;
    };

    /**
     * Multipart body for the v2 admin charities/umbrellas endpoints.
     * v2 explicit-value semantics: append when the form provided the key (undefined = untouched
     * = server preserves). false/0/'' are real values; '' clears the three URL fields server-side.
     */
    const getCharityV2FormData = (data, {isUmbrellaResource, isCreate}) => {
        const formData = new FormData();
        if (data.logo && data.logo.rawFile) formData.append('logo', data.logo.rawFile);
        // Per-resource lists (delta bulletin 2): charities never bind require_post_approval;
        // umbrellas never bind location/parent/coordinator/project-level fields;
        // default_project_coordinator_id is UPDATE-ONLY (create → 400).
        const SHARED = ['charity_name', 'short_name', 'description', 'email', 'city', 'country',
            'website_link', 'payment_link_url', 'paypal_payment_link_url', 'child_detail_base_url',
            'active', 'demo_charity', 'send_new_post_mail',
            'allow_text_messages', 'allow_photo_messages', 'allow_video_messages',
            'default_timezone', 'default_currency', 'default_locale'];
        const CHARITY_ONLY = ['sponsorship_amount', 'nr_of_countries', 'nr_of_projects', 'lat', 'lng',
            'days_orange_alert', 'days_red_alert', 'location_id', 'parent_charity_external_id'];
        const fields = isUmbrellaResource
            ? [...SHARED, 'require_post_approval', 'send_registration_copy_to_projects']
            : (isCreate ? [...SHARED, ...CHARITY_ONLY]
                        : [...SHARED, ...CHARITY_ONLY, 'default_project_coordinator_id']);
        fields.forEach((key) => {
            if (data[key] !== undefined && data[key] !== null) formData.append(key, data[key]);
        });
        if (data.metadata && typeof data.metadata === 'object') {
            formData.append('metadata', JSON.stringify(data.metadata));
        }
        return formData;
    };

    const getPostFormData = (postData) => {
        let formData = new FormData();

        if (postData.child_id) {
            formData.append('child_id', postData.child_id);
        }

        // Handle message_type to media_type conversion
        if (postData.message_type) {
            formData.append('message_type', postData.message_type);
            
            // For PHOTO and VIDEO types, we need to set media_type for backward compatibility
            if (postData.message_type === 'PHOTO') {
                formData.append('media_type', 'photo');
                formData.append('portrait_mode', postData.portrait_mode);
                if (postData.image) {
                    formData.append('image', postData.image.rawFile);
                }
            } else if (postData.message_type === 'VIDEO') {
                formData.append('media_type', 'video');
                formData.append('portrait_mode', postData.portrait_mode);
                if (postData.video) {
                    formData.append('video', postData.video.rawFile);
                }
            } else if (postData.message_type === 'TEXT') {
                // For TEXT type, we still need to send media_type for the admin endpoint
                formData.append('media_type', 'text');
            }
        } else if (postData.media_type) {
            // Fallback for old format (if any)
            formData.append('media_type', postData.media_type);
            formData.append('portrait_mode', postData.portrait_mode);
            if (postData.image) {
                formData.append('image', postData.image.rawFile);
            }
            if (postData.video) {
                formData.append('video', postData.video.rawFile);
            }
        }

        formData.append("description", postData.description);
        
        // Add status field if present (for status updates like REJECTED -> PUBLISHED)
        if (postData.status) {
            formData.append('status', postData.status);
        }
        
        // Add rejection_reason field if present (for rejecting published posts)
        if (postData.rejection_reason) {
            formData.append('rejectionReason', postData.rejection_reason);
        }

        return formData;
    }

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        const {json} = response;
        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
                if (!json.hasOwnProperty("page")) {
                    throw new Error(
                        "The page property must be must be present in the Json response"
                    );
                }
                if (!json.page.hasOwnProperty("totalElements")) {
                    throw new Error(
                        "The page.totalElements property must be must be present in the Json response"
                    );
                }

                /* The (HAL) data is contained in the _embedded section of the JSON structure
                 * and then specified by the resource (like children), for example:
                 * { "_embedded" : { "children" : [] } }
                 *
                 * Make sure to return an empty array if no data is found.
                 */
                const targetResource = mapResource(resource, resourceMapper);
                return {
                    data: json.hasOwnProperty('_embedded') ? json._embedded[targetResource] : [],
                    total: parseInt(json.page.totalElements, 10)
                };
            case CREATE:
                return {data: json, id: json.id};
            default:
                return {data: json};
        }
    };

    const toCamel = (s) => {
        return s.replace(/([-_][a-z])/ig, ($1) => {
            return $1.toUpperCase()
                .replace('-', '')
                .replace('_', '');
        });
    };



    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        const role = localStorage.getItem('role');
        const rolePath = role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';

        if (resource === 'children' && (type === UPDATE_MANY || type === DELETE_MANY)) {
            return Promise.reject(new Error(
                'Bulk children operations are not supported — the v2 children endpoints are single-record'
            ));
        }

        // guardians/volunteers/umbrellaAdmins now live fully on v2 admin endpoints
        // (opaque Keycloak-string ids). Bulk verbs are rejected outright — the v2
        // endpoints are single-record only (children/charities precedent).
        if ((resource === 'guardians' || resource === 'volunteers' || resource === 'umbrellaAdmins')
            && (type === UPDATE_MANY || type === DELETE_MANY)) {
            return Promise.reject(new Error(
                `Bulk ${resource} operations are not supported — the v2 ${resource} endpoints are single-record`
            ));
        }

        // guardians/umbrellaAdmins have no v2 GET_MANY endpoint at all — zero
        // consumers (FE-confirmed). Reject client-side rather than hit a route that
        // doesn't exist. (volunteers DOES have a v2 GET_MANY — see below.)
        if ((resource === 'guardians' || resource === 'umbrellaAdmins') && type === GET_MANY) {
            return Promise.reject(new Error(`${resource} has no v2 GET_MANY endpoint`));
        }

        // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === UPDATE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${rolePath}/${getUrlResource(resource)}/${id}`, {
                        method: "PUT",
                        body: JSON.stringify(params.data)
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json)
            }));
        }
        // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${rolePath}/${getUrlResource(resource)}/${id}`, {
                        method: "DELETE"
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json)
            }));
        }

        // Children now live fully on the v2 admin endpoints (UUID ids). The flat v2 admin
        // response shape IS the canonical record shape — no more v1 HATEOAS cache-shape shim.
        if (resource === 'children' && type === CREATE) {
            return httpClient(`${apiV2Url}/${rolePath}/children`, {
                method: 'POST',
                body: getChildV2FormData(params.data),
            }).then(({ json }) => ({ data: json }));
        }
        if (resource === 'children' && type === UPDATE) {
            const externalId = isUuid(params.id)
                ? params.id
                : (params.data.external_id || (params.previousData && params.previousData.external_id));
            if (!externalId) {
                return Promise.reject(new Error(
                    'Child record has no external_id — cannot call the v2 update endpoint'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/children/${externalId}`, {
                method: 'PUT',
                body: getChildV2FormData(params.data),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'children' && type === DELETE) {
            return httpClient(`${apiV2Url}/${rolePath}/children/${params.id}`, {
                method: 'DELETE',
            }).then(() => ({ data: { id: params.id } }));
        }

        // Charities writes live fully on v2 for every role (UUID ids, explicit-value multipart).
        if (resource === 'charities' && type === CREATE) {
            return httpClient(`${apiV2Url}/${rolePath}/charities`, {
                method: 'POST',
                body: getCharityV2FormData(params.data, { isUmbrellaResource: false, isCreate: true }),
            }).then(({ json }) => ({ data: json }));
        }
        if (resource === 'charities' && type === UPDATE) {
            const externalId = isUuid(params.id)
                ? params.id
                : (params.data.external_id || (params.previousData && params.previousData.external_id));
            if (!externalId) {
                return Promise.reject(new Error(
                    'Charity record has no external_id — cannot call the v2 update endpoint'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/charities/${externalId}`, {
                method: 'PUT',
                body: getCharityV2FormData(params.data, { isUmbrellaResource: false, isCreate: false }),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'charities' && type === DELETE) {
            // The v2 delete endpoint is umbrellad-only by design (admin/charitad 405 server-side).
            // Reject client-side for any other role rather than firing a request we know will fail.
            if (role !== 'umbrella') {
                return Promise.reject(new Error(
                    'Only an umbrella admin can delete a charity (the endpoint rejects other roles)'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/charities/${params.id}`, {
                method: 'DELETE',
            }).then(() => ({ data: { id: params.id } }))
                .catch((error) => {
                    // The server sends `include-message: always`, so fetchJson's HttpError
                    // already carries the 409 guard explanation (children/volunteers/supported
                    // charities) in error.message — pass it through untouched. Only fall back to
                    // a fixed sentence for the edge case of a 409 with no usable message.
                    if (error.status === 409 && !error.message) {
                        throw new Error('Charity has active children, volunteers or supported charities');
                    }
                    throw error;
                });
        }

        // Umbrellas writes are admin-only — the v2 route only exists under /admin/umbrellas;
        // for any other role there is no matching route (unmapped path → 404 server-side), so
        // reject client-side instead of firing a request we know will fail. In practice the UI
        // hides the umbrellas resource from every other role anyway (see auth/hasPermission.js).
        if (resource === 'umbrellas' && type === CREATE) {
            if (role !== 'admin') {
                return Promise.reject(new Error(
                    'Only a Nofam admin can create an umbrella (no v2 route exists for other roles)'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellas`, {
                method: 'POST',
                body: getCharityV2FormData(params.data, { isUmbrellaResource: true, isCreate: true }),
            }).then(({ json }) => ({ data: json }));
        }
        // The umbrella's three notification switches (OUTCOME item 10): the umbrella admin on their
        // own umbrella, the Nofam admin on any. Called as dataProvider.updateNotifications.
        // Contract: nofam-api .ai/docs/integration/nofam-api-v2.md (/api/v2 admin routes, umbrella notification switches)
        // Pending post text (#52): the moderation queue edits a post before approving it; the API
        // retranslates and answers the pending list's item. Called as dataProvider.updateDescription.
        // Contract: nofam-api .ai/docs/integration/nofam-api-v2.md (/api/v2 admin routes, pending post text)
        if (resource === 'posts' && type === 'updateDescription') {
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error(
                    'Only a Nofam admin or umbrella admin can edit a pending post'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/posts/${params.id}/description`, {
                method: 'PUT',
                body: getPostDescriptionV2JsonData(params.data),
            }).then(({ json }) => ({ data: { ...json, id: json?.id ?? params.id } }));
        }
        if (resource === 'umbrellas' && type === 'updateNotifications') {
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error(
                    'Only a Nofam admin or umbrella admin can change the umbrella notifications'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellas/${params.id}/notifications`, {
                method: 'PUT',
                body: getUmbrellaNotificationsV2JsonData(params.data),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'umbrellas' && type === UPDATE) {
            if (role !== 'admin') {
                return Promise.reject(new Error(
                    'Only a Nofam admin can update an umbrella (no v2 route exists for other roles)'
                ));
            }
            const externalId = isUuid(params.id)
                ? params.id
                : (params.data.external_id || (params.previousData && params.previousData.external_id));
            if (!externalId) {
                return Promise.reject(new Error(
                    'Umbrella record has no external_id — cannot call the v2 update endpoint'
                ));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellas/${externalId}`, {
                method: 'PUT',
                body: getCharityV2FormData(params.data, { isUmbrellaResource: true, isCreate: false }),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }

        // Guardians write fully on v2 (JSON, explicit-value semantics). CREATE is left
        // to rethrow a server error untouched (no .catch on the httpClient call) — the
        // 409 duplicate-email body (existing_guardian_id) must survive intact for the
        // Task 3 attach recipe. The builder itself can throw synchronously (missing/
        // non-UUID child_id) — that must still surface as a rejected Promise, not a
        // synchronous throw out of the data provider, so it's built outside the
        // httpClient call and funneled through Promise.reject on failure.
        if (resource === 'guardians' && type === CREATE) {
            // Client-side guard (belt-and-braces): guardian writes are admin + umbrella
            // only — reject charitad, mirroring the umbrellas precedent above.
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error('Only a Nofam admin or umbrella admin can create a guardian'));
            }
            let body;
            try {
                body = getGuardianV2JsonData(params.data, { isCreate: true });
            } catch (e) {
                return Promise.reject(e);
            }
            return httpClient(`${apiV2Url}/${rolePath}/guardians`, {
                method: 'POST',
                body,
            }).then(({ json }) => ({ data: json }));
        }
        if (resource === 'guardians' && type === UPDATE) {
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error('Only a Nofam admin or umbrella admin can update a guardian'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/guardians/${params.id}`, {
                method: 'PUT',
                body: getGuardianV2JsonData(params.data, { isCreate: false, previousData: params.previousData }),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'guardians' && type === DELETE) {
            // Client-side guard (belt-and-braces; the delete button is already hidden
            // for non-admin roles) — guardian delete is admin-only (D7).
            if (role !== 'admin') {
                return Promise.reject(new Error('Only a Nofam admin can delete a guardian'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/guardians/${params.id}`, {
                method: 'DELETE',
            }).then(() => ({ data: { id: params.id } }));
        }

        // Volunteers write fully on v2 (JSON).
        if (resource === 'volunteers' && type === CREATE) {
            // Client-side guard (belt-and-braces): volunteer writes are admin + umbrella
            // only — reject charitad, mirroring the umbrellas precedent above.
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error('Only a Nofam admin or umbrella admin can create a volunteer'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/volunteers`, {
                method: 'POST',
                body: getVolunteerV2JsonData(params.data, { isCreate: true }),
            }).then(({ json }) => ({ data: json }));
        }
        if (resource === 'volunteers' && type === UPDATE) {
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error('Only a Nofam admin or umbrella admin can update a volunteer'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/volunteers/${params.id}`, {
                method: 'PUT',
                body: getVolunteerV2JsonData(params.data, { isCreate: false }),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'volunteers' && type === DELETE) {
            // Client-side guard (belt-and-braces): volunteer delete is admin + umbrella
            // only — reject charitad (whitelist form, matches the create/update guards
            // above and the guardians/umbrellaAdmins admin-only guards elsewhere).
            if (role !== 'admin' && role !== 'umbrella') {
                return Promise.reject(new Error('Only a Nofam admin or umbrella admin can delete a volunteer'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/volunteers/${params.id}`, {
                method: 'DELETE',
            }).then(() => ({ data: { id: params.id } }));
        }

        // UmbrellaAdmins write fully on v2 (JSON), admin-only per the contract (writes
        // + resetPassword) — client-side guards below are belt-and-braces.
        if (resource === 'umbrellaAdmins' && type === CREATE) {
            if (role !== 'admin') {
                return Promise.reject(new Error('Only a Nofam admin can create an umbrella admin'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellaAdmins`, {
                method: 'POST',
                body: getUmbrellaAdminV2JsonData(params.data, { isCreate: true }),
            }).then(({ json }) => ({ data: json }));
        }
        if (resource === 'umbrellaAdmins' && type === UPDATE) {
            if (role !== 'admin') {
                return Promise.reject(new Error('Only a Nofam admin can update an umbrella admin'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellaAdmins/${params.id}`, {
                method: 'PUT',
                body: getUmbrellaAdminV2JsonData(params.data, { isCreate: false }),
            }).then(({ json }) => ({ data: { ...json, id: params.id } }));
        }
        if (resource === 'umbrellaAdmins' && type === DELETE) {
            if (role !== 'admin') {
                return Promise.reject(new Error('Only a Nofam admin can delete an umbrella admin'));
            }
            return httpClient(`${apiV2Url}/${rolePath}/umbrellaAdmins/${params.id}`, {
                method: 'DELETE',
            }).then(() => ({ data: { id: params.id } }));
        }

        const {url, options} = convertDataRequestToHTTP(type, resource, params);
        return httpClient(url, options).then(response => convertHTTPResponse(response, type, resource, params));
    };
};

// Custom action handlers for post moderation
const customActionHandlers = {
    approvePost: (apiUrl, httpClient, postId) => {
        const role = localStorage.getItem('role');
        const rolePath = role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';
        const url = `${apiUrl}/${rolePath}/posts/${postId}/approve`;
        
        return httpClient(url, {
            method: 'PUT'
        }).then(response => ({ data: { id: postId, status: 'PUBLISHED' } }));
    },
    
    rejectPost: (apiUrl, httpClient, postId, reason) => {
        const role = localStorage.getItem('role');
        const rolePath = role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';
        const url = `${apiUrl}/${rolePath}/posts/${postId}/reject`;
        
        return httpClient(url, {
            method: 'PUT',
            body: JSON.stringify({ reason })
        }).then(response => ({ data: { id: postId, status: 'REJECTED' } }));
    }
};

// Export the main function with custom actions attached
const dataProvider = springDataRest;
dataProvider.customActions = customActionHandlers;

export default dataProvider;
